import { useApolloClient, useMutation } from "@apollo/client/react";
import { type ApolloCache } from "@apollo/client/cache";
import { AddPinsDocument, RemovePinsDocument } from "@/graphql/generated/operations";

export interface PinAddInput {
  personIds?: string[];
  sharedItemIds?: string[];
  groups?: string[];
  removePinsFromOtherGroups?: boolean;
}

export interface PinRemoveInput {
  personIds?: string[];
  sharedItemIds?: string[];
}

export interface UsePinResult {
  add: (input: PinAddInput) => Promise<void>;
  remove: (input: PinRemoveInput) => Promise<void>;
}

function flipIsPinned(
  cache: ApolloCache,
  personIds: string[] | undefined,
  next: boolean,
) {
  for (const id of personIds ?? []) {
    cache.modify({
      id: cache.identify({ __typename: "Person", id }),
      fields: { isPinned: () => next },
    });
  }
}

export function usePin(): UsePinResult {
  const [addMut] = useMutation(AddPinsDocument);
  const [removeMut] = useMutation(RemovePinsDocument);
  const client = useApolloClient();

  async function add(input: PinAddInput) {
    const res = await addMut({
      variables: {
        input: {
          persons: input.personIds,
          sharedItems: input.sharedItemIds,
          groups: input.groups,
          removePinsFromOtherGroups: input.removePinsFromOtherGroups,
        },
      },
    });
    const out = res.data?.addPins;
    if (out?.__typename !== "AddPinsResult") {
      throw new Error(
        out?.__typename === "DailyQuotaExceeded"
          ? "DailyQuotaExceeded"
          : "AddPins rejected",
      );
    }
    flipIsPinned(client.cache, input.personIds, true);
    if (input.groups !== undefined) {
      await client.refetchQueries({ include: ["PinGroupMemberships"] });
    }
  }

  async function remove(input: PinRemoveInput) {
    const res = await removeMut({
      variables: {
        input: { persons: input.personIds, sharedItems: input.sharedItemIds },
      },
    });
    const out = res.data?.removePins;
    if (out?.__typename !== "RemovePinsResult") {
      throw new Error("RemovePins rejected");
    }
    flipIsPinned(client.cache, input.personIds, false);
  }

  return { add, remove };
}
